import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/admin/AdminAuthContext";

// API calls use relative `/api/...` paths. See ContentProvider.jsx.

/**
 * Handles the return leg of the Emergent Google OAuth flow:
 *   .../admin/callback#session_id=abc123
 *
 * On mount it:
 *   1. synchronously pulls `session_id` from window.location.hash
 *   2. POSTs it to /api/admin/auth/session (exchanges for our 7-day cookie)
 *   3. strips the fragment from the URL (so refresh is clean)
 *   4. refreshes the AdminAuthContext and navigates to /admin
 *
 * A useRef flag guarantees we only exchange the session once, even under
 * React StrictMode double-invocation in development.
 */
export default function AdminAuthCallback() {
  const { refresh } = useAdminAuth();
  const navigate = useNavigate();
  const ran = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const hash = typeof window !== "undefined" ? window.location.hash || "" : "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError("Missing session token from Google.");
      return;
    }

    (async () => {
      try {
        const res = await fetch(`/api/admin/auth/session`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });

        // Remove the fragment immediately, before any further navigation,
        // so the session_id never lingers in the browser history.
        if (typeof window !== "undefined" && window.history?.replaceState) {
          const clean = window.location.pathname + window.location.search;
          window.history.replaceState(null, "", clean);
        }

        if (res.status === 403) {
          setError("This Google account is not authorised for admin access.");
          return;
        }
        if (!res.ok) {
          setError("Sign-in failed. Please try again.");
          return;
        }

        await refresh();
        navigate("/admin", { replace: true });
      } catch {
        setError("Network error while completing sign-in.");
      }
    })();
  }, [refresh, navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-white px-4"
      data-testid="admin-auth-callback"
    >
      <div className="w-full max-w-md text-center">
        {error ? (
          <>
            <h1 className="sas-h3" data-testid="admin-auth-callback-error">
              {error}
            </h1>
            <button
              type="button"
              onClick={() => navigate("/admin/login", { replace: true })}
              className="sas-btn sas-btn-ghost mt-6"
              data-testid="admin-auth-callback-retry"
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <div className="text-sm text-[color:var(--sas-ink-2)]">Completing sign-in…</div>
        )}
      </div>
    </div>
  );
}
